import {Label} from '@primer/react'
import type {LabelProps, LabelColorOptions} from '@primer/react'
import type {SignatureResult, VerificationStatus} from './signed-commit-types'
import {SignedCommitDialog, SignedCommitErrorDialog} from './SignedCommitDialog'
import {useState, useRef} from 'react'

import styles from './SignedCommitBadge.module.css'

export interface SignedCommitBadgeProps {
  hasSignature: boolean
  verificationStatus?: VerificationStatus
  signature?: SignatureResult
  badgeSize?: LabelProps['size']
  commitOid: string
  helpUrl?: string
}

export function SignedCommitBadge({
  hasSignature,
  verificationStatus,
  signature,
  commitOid,
  badgeSize = 'small',
  helpUrl,
}: SignedCommitBadgeProps) {
  const [isOpen, setIsOpen] = useState(false)
  const badgeRef = useRef<HTMLButtonElement>(null)

  const isError = !verificationStatus || !signature

  if (!isError && verificationStatus === 'unsigned') {
    return <></>
  }

  const onDismiss = () => {
    setIsOpen(false)
    setTimeout(() => {
      badgeRef.current?.focus()
    }, 0)
  }

  const labelProps = {
    as: 'button' as const,
    onClick: () => setIsOpen(true),
    ref: badgeRef,
    size: badgeSize,
    className: styles.clickableLabel,
  }

  return (
    <>
      <VerificationLabel labelProps={labelProps} verificationStatus={isError ? 'error' : verificationStatus} />
      {isOpen &&
        (isError ? (
          <SignedCommitErrorDialog
            isOpen={isOpen}
            onDismiss={onDismiss}
            helpUrl={signature?.helpUrl ?? helpUrl}
            ariaLabel={`Verification error: ${commitOid.slice(0, 7)}`}
          />
        ) : (
          <SignedCommitDialog
            isOpen={isOpen}
            onDismiss={onDismiss}
            verificationStatus={verificationStatus}
            signature={signature}
            hasSignature={hasSignature}
            ariaLabel={`${verificationStatus.replace('_', ' ')}: ${commitOid.slice(0, 7)}`}
          />
        ))}
    </>
  )
}

const STATUS_TO_LABEL = {
  verified: {
    label: 'Verified',
    variant: 'success',
  },
  unverified: {
    label: 'Unverified',
    variant: 'attention',
  },
  partially_verified: {
    label: 'Partially verified',
    variant: 'success',
  },
  error: {
    label: 'Verification error',
    variant: 'secondary',
  },
}

const VerificationLabel = ({
  ref,
  verificationStatus,
  labelProps,
}: {
  verificationStatus: VerificationStatus | 'error'
  labelProps: LabelProps
  ref?: React.Ref<HTMLSpanElement>
}) => {
  const labelAndVariant = STATUS_TO_LABEL[verificationStatus as keyof typeof STATUS_TO_LABEL]

  if (!labelAndVariant) {
    return <></>
  }

  return (
    <Label variant={labelAndVariant.variant as LabelColorOptions} {...labelProps} ref={ref}>
      {labelAndVariant.label}
    </Label>
  )
}

VerificationLabel.displayName = 'VerificationLabel'
